import forge from 'node-forge';
import { call, put, takeEvery } from 'redux-saga/effects';

import { getEnvironmentConfig, getLogger } from '../../utils/core';
import { resourceSagaUtils, resourceStoreTypes } from '../core';

import * as actions from './actions';

const log = getLogger('flux/key-management/sagas');

// helper function for fetching the encryption key and verifying the signature
function* getEncryptionKey(path) {
  let isSecure = false;
  let encryptionKey = {};
  try {
    // first we need to create the signaturePublicKey, used to verify the signature from qcs
    assert(getEnvironmentConfig().signature_public_key, 'public key for signature not found #encrypt');
    const signaturePublicKey = forge.pki.publicKeyFromPem(
      `-----BEGIN PUBLIC KEY-----${getEnvironmentConfig().signature_public_key}-----END PUBLIC KEY-----`,
    );

    // then fetch key from qcs
    const config = resourceStoreTypes.mkQcsSyncResourceConfig({
      resourceName: 'key-management-system',
      resourceBaseUrl: `/key-management/get-kms-public-key/${path}`,

      successAction: actions.getEncryptionKeySuccess,
      failureAction: actions.getEncryptionKeyFailure,
    });
    const keyResponse = yield call(resourceSagaUtils.qcsSyncGetResource, config, {});

    if (
      assert(signaturePublicKey) &&
      assert(keyResponse?.status === 200, 'did not get 200 response for key request') &&
      assert(keyResponse?.data, 'no data in key request') &&
      assert(keyResponse?.data?.signatureAlgorithm === 'SHA256withRSA', 'unexpected signature algorithm')
    ) {
      encryptionKey = keyResponse.data;

      // now verify the key as signed properly and by qcs
      const dataToVerify = forge.md.sha256.create().update(encryptionKey.publicKey, 'utf8');
      const verify = signaturePublicKey.verify(
        dataToVerify.digest().bytes(),
        forge.util.decode64(encryptionKey.signature),
      );

      assert(verify, 'encryption key signature verification failed');
      isSecure = verify;
    } else {
      isSecure = false;
    }
  } catch (error) {
    isSecure = false;
    log.error('Error in fetching and checking public key signature: ', error);
  }

  return { isSecure, ...encryptionKey };
}

// helper function that uses the public key to encrypt a value
function tryToEncryptValue(rawPublicKey, value) {
  let isSecure = false;
  let encryptedValue;

  try {
    assert(Boolean(value), 'no value passed');
    assert(Boolean(rawPublicKey), 'no raw public key passed');
    const pem = `-----BEGIN PUBLIC KEY-----${rawPublicKey}-----END PUBLIC KEY-----`;
    const publicKey = forge.pki.publicKeyFromPem(pem);
    encryptedValue = publicKey.encrypt(forge.util.encodeUtf8(value));
    encryptedValue = btoa(encryptedValue);
    isSecure = true;
  } catch (error) {
    isSecure = false;
    encryptedValue = null;
    log.log('Error encrypting value: ', error);
  }

  return { isSecure, encryptedValue };
}

function* encryptDataActionWatcher() {
  yield takeEvery(actions.encryptData, function* encryptData(action) {
    const { isSecure: isEncryptionKeySecure, publicKey } = yield getEncryptionKey(action.payload.kmsPath);
    let isTotallySecure = isEncryptionKeySecure;
    const encryptedValues = {};
    if (assert(isEncryptionKeySecure, 'encryption key not secure')) {
      Object.entries(action.payload.values).forEach(([key, value]) => {
        const { isSecure: isEncryptedValueSecure, encryptedValue } = tryToEncryptValue(publicKey, value);
        isTotallySecure = isTotallySecure && isEncryptedValueSecure;
        if (assert(isEncryptedValueSecure, 'encrypted value not secure')) {
          encryptedValues[`${key}Encrypted`] = encryptedValue;
        }
      });
    }

    const retObject = {
      isSecure: isTotallySecure,
      encryptedValues,
    };

    // expects actions.encryptData to be used with bindPromiseAction
    action?.meta?.[isTotallySecure ? 'resolve' : 'reject']?.(retObject);

    // also dispatch success/failure action
    yield put((isTotallySecure ? actions.encryptDataSuccess : actions.encryptDataFailure)(retObject));
  });
}

export default [encryptDataActionWatcher];
